import { paramCase } from 'change-case';
import {
    classDictionary,
    FlexClassDefinition,
    tailwindConfig,
    tailwindDefinitions,
    tailwindSideCharacterMap,
} from '@/data/tailwind';

/**
 * Returns the Flex class definition for the passed-in class. See tailwind.js for additional details.
 *
 * Args:
 *      className (String): The class you want to get the definition for.
 *
 * Returns:
 *      (Object) An instance of FlexClassDefinition. If no definition is found, an empty object is returned.
 *
 * Example:
 *      getClassDefinition('f-text-black'); // => { tailwindDefinitionProp: 'textColor', value: '#000000', ... }
 */
export function getClassDefinition(className) {
    return classDictionary[className] || new FlexClassDefinition({});
}

/**
 * Returns the Tailwind class definition value for a given class.
 *
 * Each Tailwind class has a "class definition" which contains metadata about the class. See tailwind.js for more details.
 *
 * Args:
 *      className (String): The class you want to get the definition for.
 *
 * Returns:
 *      (Object) An instance of ClassDictionaryDefinition.
 *
 * Example:
 *      getClassDefinition('f-text-black'); // => { tailwindDefinitionProp: 'textColor', value: '#000000', ... }
 */
export function getTailwindThemeObject(themeProp) {
    return tailwindConfig.theme[themeProp];
}

/**
 * A compare function for sorting Tailwind theme object properties.
 *
 * Example:
 *      Object.values(tailwindConfig.theme.padding).sort(tailwindThemePropSort);
 */
export function tailwindThemePropSort(a, b) {
    if (typeof a === 'string') a = a.replace(/(?:[a-zA-Z]|%)/g, '');
    if (typeof b === 'string') b = b.replace(/(?:[a-zA-Z]|%)/g, '');

    return a - b;
}

/**
 * Returns the CSS property name(s) for a base CSS property and side.
 *
 * This method is used to help determine which CSS properties are affected by a given Tailwind class.
 *
 * Args:
 *      baseCssProp (String): The "base" portion of the CSS property. e.g. the base of "padding-left" is "padding"
 *      side (String): The side. e.g. "l" for "left".
 *
 * Returns:
 *      (Array) An array of CSS properties.
 *
 * Example:
 *      getCssProperties('borderRadius', 'l'); // => ['border-top-left-radius', 'border-bottom-left-radius']
 */
export function getCssProperties(baseCssProp, side) {
    const cssProp = paramCase(baseCssProp);
    const cssProps = [];

    switch (true) {
        case cssProp === 'text-decoration':
            cssProps.push('text-decoration-line');
            break;

        case cssProp === 'border-radius' && side === 'l':
            cssProps.push(`border-${ tailwindSideCharacterMap.tl }-radius`);
            cssProps.push(`border-${ tailwindSideCharacterMap.bl }-radius`);
            break;

        case cssProp === 'border-radius' && side === 'r':
            cssProps.push(`border-${ tailwindSideCharacterMap.tr }-radius`);
            cssProps.push(`border-${ tailwindSideCharacterMap.br }-radius`);
            break;

        case side === 'x':
            cssProps.push(`${ cssProp }-${ tailwindSideCharacterMap.l }`);
            cssProps.push(`${ cssProp }-${ tailwindSideCharacterMap.r }`);
            break;

        case side === 'y':
            cssProps.push(`${ cssProp }-${ tailwindSideCharacterMap.t }`);
            cssProps.push(`${ cssProp }-${ tailwindSideCharacterMap.b }`);
            break;

        default:
            cssProps.push(cssProp);
    }

    return cssProps;
}

/**
 * Gets the Tailwind theme object key that matches the specified value.
 *
 * Args:
 *      value (String): The value to match against.
 *      tailwindThemeObject (Object): The Tailwind theme object.
 *
 * Returns:
 *      (String) The key that matches the specified value.
 *
 * Example:
 *      getKeyByValue('700', tailwindConfig.theme.fontWeight); // => 'bold'
 */
export function getKeyByValue(value, tailwindThemeObject) {
    let foundKey = '';

    Object.keys(tailwindThemeObject).forEach((key) => {
        let targetValue = value;
        let tailwindValue = tailwindThemeObject[key];

        if (typeof targetValue === 'string') targetValue = targetValue.toLowerCase();
        if (typeof tailwindValue === 'string') tailwindValue = tailwindValue.toLowerCase();

        if (tailwindValue === targetValue) foundKey = key;

        // check for nested keys, e.g. the color shades
        if (typeof tailwindValue === 'object') {
            Object.entries(tailwindValue).forEach((entry) => {
                const [nestedKey, nestedValue] = entry;

                if (nestedValue.toLowerCase() === targetValue) {
                    foundKey = `${ key }-${ nestedKey }`;
                }
            });
        }
    });

    return foundKey;
}

/**
 * Gets the Tailwind theme object value that matches the specified key.
 *
 * Args:
 *      key (String): The key to match against.
 *      tailwindThemeObject (Object): The Tailwind theme object.
 *
 * Returns:
 *      (String) The value that matches the specified key.
 *
 * Example:
 *      getValueByKey('bold', tailwindConfig.theme.fontWeight); // => '700'
 */
export function getValueByKey(key, tailwindThemeObject) {
    if (key.includes('-')) {
        // nested color prop
        const prop = key.slice(0, key.lastIndexOf('-'));
        const subprop = key.slice(key.lastIndexOf('-') + 1);

        return tailwindThemeObject[prop][subprop];
    }

    return tailwindThemeObject[key] || '';
}

/**
 * Generates a Tailwind class that matches the specified criteria.
 *
 * At minimum, the tailwindDefinitionProp is required.
 *
 * Args:
 *      value (String): The CSS value that the class outputs.
 *      side (String): The side that the class affects (e.g. "r" if it's a border-right class).
 *      state (String): The state that the class affects (e.g. "hover").
 *      screen (String): The screen that the class affects (e.g. "mob" for mobile).
 *      tailwindDefinitionProp (Object): The name of the Tailwind definition that the class belongs to.
 *
 * Returns:
 *      (String) The generated Tailwind class.
 *
 * Example:
 *      const className = generateClass({
 *          value: '1rem',
 *          side: 'l',
 *          screen: 'mob',
 *          tailwindDefinitionProp: 'padding',
 *      });
 *
 *      console.log(className); // => 'mob:f-pl-1'
 */
export function generateClass(data) {
    const {
        key,
        value,
        side,
        state,
        screen,
        tailwindDefinitionProp,
    } = data;

    const tailwindThemeObject = getTailwindThemeObject(tailwindDefinitionProp);
    const tailwindDefinition = tailwindDefinitions[tailwindDefinitionProp];

    const screenStr = screen === 'all' || !screen ? '' : `${ screen }:`;
    const stateStr = state === 'default' || !state ? '' : `${ state }:`;
    const prefix = tailwindDefinition.prefix;
    const negativeValue = value && value.startsWith('-');

    let baseStr = tailwindDefinition.base;
    let keyStr = key || getKeyByValue(value, tailwindThemeObject);

    if (negativeValue) {
        baseStr = `-${ baseStr }`;
        keyStr = keyStr.slice(1);
    }

    if (baseStr) {
        // some classes don't have a "base", like f-block, where the signature is {prefix}{key}
        // some classes do, like f-text-white, where the signature is {prefix}{base}{key}
        // in the latter case, we append a hyphen. If we didn't, it would output "f-textwhite"
        keyStr = `-${ keyStr }`;
    }

    if (!key && !value) {
        // some custom utility classes don't have a key or value, e.g. ".fu-boxed" which has a signature of .{prefix}-{base}
        // in this case we don't a hyphen appended to the end by keyStr
        keyStr = '';
    }

    let sideStr = side ? `-${ side }` : '';

    if (baseStr.match('m|p')) {
        sideStr = sideStr.replace('-', '');
    }

    // Tailwind class signature: .{screen:?}{state:?}{prefix}{base}{-side?}{-key?}
    return screenStr + stateStr + prefix + baseStr + sideStr + keyStr;
}
